// src/components/team/ImportMillon.jsx
import React, { useState } from "react";
import { supabase } from "../../lib/supabaseClient";

const ImportMillon = () => {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Exemple d'URL Millon :
  // https://www.millon.com/catalogue/vente4113-millon-access-tableaux-du-xix-et-xxe-siecles#catalog
  function extractAuctionName(u) {
    try {
      const m = u.match(/\/catalogue\/vente\d+-([^#/?]+)/i);
      if (m) {
        const slug = m[1];
        return slug
          .split("-")
          .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
          .join(" ");
      }
      return "Vente Millon";
    } catch {
      return "Vente Millon";
    }
  }

  const handleImport = async () => {
    if (!url.trim()) {
      alert("Merci d'indiquer l'URL de la vente Millon !");
      return;
    }
    if (!/millon\.com/i.test(url)) {
      alert("L'URL doit être une page Millon (millon.com)");
      return;
    }

    setLoading(true);
    setStatus("🔍 Analyse de la page en cours...");
    setResult(null);

    try {
      const auctionName = extractAuctionName(url);

      console.log("🚀 Envoi vers Edge Function:", {
        auction_url: url,
        auction_name: auctionName,
      });

      const { data, error } = await supabase.functions.invoke("import-millon", {
        body: {
          auction_url: url,
          auction_name: auctionName,
          city: "Neuilly-sur-Marne", // par défaut ; la fonction Edge peut raffiner
          country: "France",
          date_start: new Date().toISOString().split("T")[0],
          house_name: "Millon",
        },
      });

      if (error) {
        console.error("❌ Erreur Edge Function:", error);
        throw error;
      }

      console.log("✅ Résultat:", data);
      setResult(data);
      setStatus(
        `✅ ${data.inserted || 0} lots importés (${data.skipped || 0} ignorés)`
      );
    } catch (e) {
      console.error("❌ Erreur complète:", e);
      const msg = e?.message || String(e);
      if (msg.includes("400")) {
        setStatus(
          "❌ Erreur 400: Vérifie que l'URL est correcte et que la page contient des lots"
        );
      } else if (msg.includes("500")) {
        setStatus("❌ Erreur serveur: Réessaie dans quelques instants");
      } else if (/network|fetch/i.test(msg)) {
        setStatus("❌ Erreur réseau: Vérifie ta connexion internet");
      } else {
        setStatus(`❌ Erreur: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
      <h2 className="text-xl font-semibold mb-3">🔨 Importer une vente Millon</h2>
      <p className="text-sm text-gray-600 mb-4">
        Colle l’URL complète de la page de vente Millon. L’import récupère
        automatiquement les lots (artiste, titre, n° et estimation).
      </p>

      <details className="mb-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          Voir des exemples d’URLs valides
        </summary>
        <div className="mt-2 space-y-1 pl-4">
          <div>
            ✓ https://www.millon.com/catalogue/vente4113-millon-access-tableaux-du-xix-et-xxe-siecles#catalog
          </div>
          <div>✗ https://www.millon.com/ (page d’accueil)</div>
        </div>
      </details>

      <div className="space-y-3">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.millon.com/catalogue/venteXXXX-..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          disabled={loading}
        />

        <button
          onClick={handleImport}
          disabled={loading || !url.trim()}
          className="w-full bg-black text-white px-5 py-3 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Import en cours...
            </span>
          ) : (
            "🚀 Lancer l’import"
          )}
        </button>
      </div>

      {status && (
        <div
          className={`mt-4 p-3 rounded-lg ${
            status.includes("✅")
              ? "bg-green-50 text-green-800 border border-green-200"
              : status.includes("❌")
              ? "bg-red-50 text-red-800 border border-red-200"
              : "bg-blue-50 text-blue-800 border border-blue-200"
          }`}
        >
          {status}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <span>📊</span>
            Résultat de l’import
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Maison</div>
              <div className="font-medium">ID: {result.house_id?.slice(0, 8)}...</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Vente</div>
              <div className="font-medium">ID: {result.sale_id?.slice(0, 8)}...</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots trouvés</div>
              <div className="font-medium text-blue-700">{result.lots_found || 0}</div>
            </div>
            <div className="bg-white p-3 rounded border">
              <div className="text-gray-500 text-xs mb-1">Lots insérés</div>
              <div className="font-semibold text-green-700">{result.inserted || 0}</div>
            </div>
          </div>

          {result.skipped > 0 && (
            <div className="mt-3 text-xs text-gray-600 bg-white p-2 rounded">
              ℹ️ {result.skipped} lot(s) ignoré(s) (déjà présents en base)
            </div>
          )}
        </div>
      )}

      <details className="mt-4 text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">
          ❓ L’import ne fonctionne pas ?
        </summary>
        <div className="mt-2 space-y-2 pl-4">
          <div>• Vérifie que l’URL pointe une page « Catalogue » avec des cartes de lots.</div>
          <div>• Si l’erreur persiste, regarde les logs de l’edge function dans Supabase.</div>
        </div>
      </details>
    </div>
  );
};

export default ImportMillon;
